import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { locateWrite, mutations } from '@/lib/mutations';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/states';
import type { CustomerRow } from '@/Pages/CRM/lib';
import type { CatalogItem } from '@/Pages/Catalog/lib';
import {
  CALL_CENTER_ALLOWED_TENDERS,
  apiError,
  type CallCenterOrder,
  type CallCenterOrderLine,
  type CreateCallCenterOrderDto,
  type CustomerAddress,
  type RepeatOrderDraft,
} from './lib';

interface BranchOption {
  id: string;
  name: string;
  code: string;
}

const TENDER_OPTIONS = ['CASH', 'CARD', 'WALLET', 'BANK_TRANSFER', 'VOUCHER', 'LOYALTY_POINTS'] as const;

/**
 * The order desk — branch, fulfillment, items, tender, and the create call.
 *
 * ADR-0021 §2.2 — this NEVER shows a "held for waiter" state after creating
 * an order: `CallCenterOrdersService.create` always passes `placedByGuest:
 * false`, so `heldForWaiterAt` is never stamped and the order goes straight
 * to CONFIRMED → kitchen. A submit toggle is offered because §2.1's create
 * call still separates "save as a draft" from "confirm and send now" — that
 * is an ordinary staff escalation (`orders.update` + `orders.send`), not the
 * guest-facing wait this module's orders never enter.
 *
 * Item entry is deliberately simple: itemId + quantity + a note, no modifier
 * configuration. `GET /catalog` needs `menu.view`, which ADR-0021's own
 * `CALL_CENTER_AGENT` role does NOT grant (§2.11 lists `customers.*`,
 * `orders.*`, `branches.view` only) — a real gap in the contract, called out
 * in the report rather than papered over with a permission this UI invents.
 * The item picker below checks for the key and explains the gap when it is
 * missing, rather than firing a request that is certain to 403.
 */
export default function OrderDesk({
  customer,
  repeatDraft,
  onRepeatConsumed,
}: {
  customer: CustomerRow | null;
  repeatDraft: RepeatOrderDraft | null;
  onRepeatConsumed: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canBrowseCatalog = can('menu.view');
  const canOverrideTender = can('orders.override_payment_policy');

  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [branchId, setBranchId] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState<'TAKEAWAY' | 'DELIVERY'>('TAKEAWAY');
  const [deliveryAddressId, setDeliveryAddressId] = useState('');
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [tenderPreference, setTenderPreference] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [submitNow, setSubmitNow] = useState(false);
  const [lines, setLines] = useState<CallCenterOrderLine[]>([]);

  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState<CatalogItem[]>([]);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CallCenterOrder | null>(null);
  const [transferBranchId, setTransferBranchId] = useState('');
  const [transferring, setTransferring] = useState(false);
  /**
   * A create durably queued but not yet acknowledged — a caller still on the
   * line has to be told this honestly, and it is one of two very different
   * things `!synced` can mean. See {@link create}.
   */
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: BranchOption[] }>('/branches', { params: { limit: 200 } })
      .then(({ data }) => setBranches(data.data ?? []))
      .catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    if (!customer || fulfillmentType !== 'DELIVERY') {
      setAddresses([]);
      return;
    }
    api
      .get<CustomerAddress[]>(`/call-center/customers/${customer.id}/addresses`)
      .then(({ data }) => setAddresses(data ?? []))
      .catch(() => setAddresses([]));
  }, [customer, fulfillmentType]);

  useEffect(() => {
    if (!repeatDraft) return;
    setBranchId(repeatDraft.branchId);
    if (repeatDraft.fulfillmentType === 'TAKEAWAY' || repeatDraft.fulfillmentType === 'DELIVERY') {
      setFulfillmentType(repeatDraft.fulfillmentType);
    }
    setLines(repeatDraft.items);
    onRepeatConsumed();
  }, [repeatDraft, onRepeatConsumed]);

  const searchItems = useCallback(() => {
    if (!canBrowseCatalog) return;
    api
      .get<{ data: CatalogItem[] }>('/catalog', {
        params: { search: itemSearch || undefined, limit: 20, isActive: true },
      })
      .then(({ data }) => setItemResults(data.data ?? []))
      .catch(() => setItemResults([]));
  }, [itemSearch, canBrowseCatalog]);

  useEffect(() => {
    if (!canBrowseCatalog) return;
    const timer = setTimeout(searchItems, 250);
    return () => clearTimeout(timer);
  }, [searchItems, canBrowseCatalog]);

  const itemNameOf = useMemo(() => {
    const map = new Map(itemResults.map((i) => [i.id, i.name]));
    return (id: string) => map.get(id) ?? id;
  }, [itemResults]);

  const addLine = (item: CatalogItem) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.itemId === item.id && !l.variantId);
      if (existing) {
        return prev.map((l) =>
          l === existing ? { ...l, quantity: (l.quantity ?? 1) + 1 } : l,
        );
      }
      return [...prev, { itemId: item.id, quantity: 1 }];
    });
  };

  const setQty = (index: number, qty: number) => {
    setLines((prev) =>
      prev.map((l, i) => (i === index ? { ...l, quantity: Math.max(1, qty) } : l)).filter((l) => (l.quantity ?? 1) > 0),
    );
  };

  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const reset = () => {
    setBranchId('');
    setFulfillmentType('TAKEAWAY');
    setDeliveryAddressId('');
    setTenderPreference('');
    setNotes('');
    setSubmitNow(false);
    setLines([]);
    setCreated(null);
    setPendingOperationId(null);
    setRefusal(null);
  };

  const canSubmit =
    !!customer &&
    !!branchId &&
    lines.length > 0 &&
    (fulfillmentType !== 'DELIVERY' || !!deliveryAddressId);

  /**
   * §28 / ADR-010 — the order-taking funnel, not a bare `api.post`. A caller
   * is on the line for this exact call, so what they are owed on a bad
   * connection is a true answer, not a fast one: `mutations.createCallCenterOrder`
   * durably queues the write before touching the network, then tries to
   * send it. `submit()`'s `synced` covers three different outcomes — landed,
   * still queued, or refused on the merits (a sold-out item, a policy
   * check) — and only `locateWrite` can tell the second from the third, so
   * this asks rather than guessing from a caught exception the way a plain
   * `try/catch` around `api.post` would have to.
   */
  const create = async () => {
    if (!customer || !canSubmit) return;
    setCreating(true);
    setRefusal(null);
    setPendingOperationId(null);
    try {
      const dto: CreateCallCenterOrderDto = {
        customerId: customer.id,
        branchId,
        fulfillmentType,
        deliveryAddressId: fulfillmentType === 'DELIVERY' ? deliveryAddressId : undefined,
        tenderPreference: (tenderPreference || undefined) as CreateCallCenterOrderDto['tenderPreference'],
        notes: notes || undefined,
        items: lines,
        submit: submitNow,
      };
      const result = await mutations.createCallCenterOrder(dto);

      if (result.synced && result.data) {
        setCreated(result.data as unknown as CallCenterOrder);
        toast.success(t(submitNow ? 'callCenter.desk.sentToKitchen' : 'callCenter.desk.created'));
        return;
      }

      const where = await locateWrite<CallCenterOrder>(result.operationId);
      if (where.settled === 'refused') {
        // A true refusal. The form and the caller's items are left exactly
        // as they were so the agent can fix one line and press the same
        // button again, with the caller still on the phone.
        setRefusal(where.message);
      } else {
        // Genuinely offline. The write is durable — nothing is lost — but
        // it is not confirmed, and "sent" would be a lie the agent would
        // then tell the caller.
        setPendingOperationId(result.operationId);
        toast.warning(t('callCenter.desk.queued'));
      }
    } catch (e) {
      toast.error(apiError(e, t('callCenter.desk.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const checkPending = async () => {
    if (!pendingOperationId) return;
    const where = await locateWrite<CallCenterOrder>(pendingOperationId);
    if (where.settled === 'delivered') {
      toast.success(t('callCenter.desk.pendingDelivered'));
      setPendingOperationId(null);
      if (where.data) setCreated(where.data);
    } else if (where.settled === 'refused') {
      setRefusal(where.message);
      setPendingOperationId(null);
    } else {
      toast.info(t('callCenter.desk.stillQueued'));
    }
  };

  /**
   * `mutate()`, not `submit()`: the agent asked a direct question ("can this
   * go to the other branch?") and needs the server's live answer — menu
   * availability at the destination is re-checked — not a queue position.
   * `TransferOrderBranchDto` carries no `operationId` (the route has no
   * `@OperationId()`), so this durably queues with no replay key; that is
   * safe because the transfer is a no-op server-side once `branchId`
   * already matches, so a duplicate delivery re-applies to the same effect.
   */
  const transfer = async () => {
    if (!created || !transferBranchId) return;
    setTransferring(true);
    try {
      const result = await mutations.transferCallCenterOrder(created.id, transferBranchId);
      if (result.queued) {
        toast.warning(t('callCenter.desk.transferQueued'));
      } else {
        setCreated(result.data as unknown as CallCenterOrder);
        toast.success(t('callCenter.desk.transferred'));
      }
    } catch (e) {
      toast.error(apiError(e, t('callCenter.desk.transferFailed')));
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('callCenter.desk.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!customer && (
          <p className="rounded-lg border border-dashed p-3 text-sm text-ink-muted">
            {t('callCenter.desk.needCaller')}
          </p>
        )}

        {created ? (
          <OrderSummary
            order={created}
            branches={branches}
            transferBranchId={transferBranchId}
            onTransferBranchChange={setTransferBranchId}
            transferring={transferring}
            onTransfer={transfer}
            onNewOrder={reset}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('callCenter.desk.branch')} required>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={!customer}>
                  <option value="">{t('callCenter.desk.pickBranch')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('callCenter.desk.fulfillment')} required>
                <Select
                  value={fulfillmentType}
                  onChange={(e) => setFulfillmentType(e.target.value as 'TAKEAWAY' | 'DELIVERY')}
                  disabled={!customer}
                >
                  <option value="TAKEAWAY">{t('callCenter.desk.takeaway')}</option>
                  <option value="DELIVERY">{t('callCenter.desk.delivery')}</option>
                </Select>
              </Field>
            </div>

            {fulfillmentType === 'DELIVERY' && (
              <Field
                label={t('callCenter.desk.deliveryAddress')}
                required
                hint={addresses.length === 0 ? t('callCenter.desk.noAddressHint') : undefined}
              >
                <Select value={deliveryAddressId} onChange={(e) => setDeliveryAddressId(e.target.value)}>
                  <option value="">{t('callCenter.desk.pickAddress')}</option>
                  {addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label ? `${a.label} — ` : ''}
                      {[a.line1, a.area, a.city].filter(Boolean).join(', ')}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field
              label={t('callCenter.desk.tender')}
              hint={t('callCenter.desk.tenderHint')}
            >
              <Select value={tenderPreference} onChange={(e) => setTenderPreference(e.target.value)}>
                <option value="">{t('callCenter.desk.tenderUnset')}</option>
                {TENDER_OPTIONS.map((m) => {
                  const allowed = (CALL_CENTER_ALLOWED_TENDERS as string[]).includes(m);
                  const disabled = !allowed && !canOverrideTender;
                  return (
                    <option key={m} value={m} disabled={disabled}>
                      {t(`callCenter.tender.${m}`)}
                      {disabled ? ` (${t('callCenter.desk.needsOverride')})` : ''}
                    </option>
                  );
                })}
              </Select>
            </Field>

            <div className="space-y-2">
              <div className="text-sm font-medium">{t('callCenter.desk.items')}</div>
              {!canBrowseCatalog ? (
                <p className="rounded-lg border border-dashed p-3 text-xs text-ink-muted">
                  {t('callCenter.desk.noCatalogPerm')}
                </p>
              ) : (
                <>
                  <Input
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    // Enter rings up the top match — an agent typing with one
                    // hand and holding a handset with the other should never
                    // have to reach for a mouse to add the item a caller just
                    // named.
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const top = itemResults[0];
                      if (top) addLine(top);
                    }}
                    placeholder={t('callCenter.desk.searchItems')}
                    aria-label={t('callCenter.desk.searchItems')}
                  />
                  {itemResults.length > 0 && (
                    <ul className="scrollbar-thin max-h-40 divide-y overflow-y-auto rounded-lg border">
                      {itemResults.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => addLine(item)}
                            className="flex w-full items-center justify-between p-2 text-start text-sm hover:bg-hover"
                          >
                            <span>{item.name}</span>
                            <Plus className="h-4 w-4 text-ink-muted" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {lines.length === 0 ? (
                <EmptyState title={t('callCenter.desk.noItems')} />
              ) : (
                <ul className="space-y-1.5">
                  {lines.map((line, index) => (
                    <li key={`${line.itemId}-${index}`} className="flex items-center justify-between gap-2 rounded-lg border p-2 text-sm">
                      <span className="truncate">{itemNameOf(line.itemId)}</span>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="ghost" onClick={() => setQty(index, (line.quantity ?? 1) - 1)}>
                          <Minus className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                        <span className="w-6 text-center tabular-nums">{line.quantity ?? 1}</span>
                        <Button size="sm" variant="ghost" onClick={() => setQty(index, (line.quantity ?? 1) + 1)}>
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeLine(index)}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" aria-hidden />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Field label={t('callCenter.desk.notes')}>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>

            <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">{t('callCenter.desk.submitNow')}</div>
                <div className="text-xs text-ink-muted">{t('callCenter.desk.submitNowHint')}</div>
              </div>
              <Switch checked={submitNow} onChange={setSubmitNow} aria-label={t('callCenter.desk.submitNow')} />
            </div>

            {/* A true refusal — the cart and every field stay exactly as
                they were, so the agent can fix one line and press the same
                button again with the caller still on the phone. */}
            {refusal && (
              <div
                role="alert"
                className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/50 dark:text-red-200"
              >
                {refusal}
              </div>
            )}

            {/* Durably queued, not yet acknowledged — never shown as a
                success, because "sent" would be a lie the agent could pass
                on to the caller. */}
            {pendingOperationId && (
              <div
                role="status"
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
              >
                <span>{t('callCenter.desk.queuedNotice')}</span>
                <Button size="sm" variant="outline" onClick={() => void checkPending()}>
                  {t('callCenter.desk.checkStatus')}
                </Button>
              </div>
            )}

            <Button className="w-full" onClick={() => void create()} disabled={!canSubmit || creating}>
              {t(submitNow ? 'callCenter.desk.createAndSend' : 'callCenter.desk.createDraft')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function OrderSummary({
  order,
  branches,
  transferBranchId,
  onTransferBranchChange,
  transferring,
  onTransfer,
  onNewOrder,
}: {
  order: CallCenterOrder;
  branches: BranchOption[];
  transferBranchId: string;
  onTransferBranchChange: (id: string) => void;
  transferring: boolean;
  onTransfer: () => void;
  onNewOrder: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { format } = useCurrency(order.branchId);
  // ADR-0021 §2.9 — transferable only before the order reaches the kitchen
  // (no `placedAt`, still DRAFT/CONFIRMED). `OrderRow` does not carry
  // `placedAt`, so status is the client-side proxy; the server re-checks the
  // real condition and answers 400 if it has already fired.
  const canTransfer = can('orders.transfer') && (order.status === 'DRAFT' || order.status === 'CONFIRMED');

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-accent bg-accent-soft p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{order.number}</span>
          <Badge tone={order.status === 'CANCELLED' || order.status === 'VOIDED' ? 'danger' : 'success'}>
            {order.status}
          </Badge>
        </div>
        <div className="mt-1 text-sm text-ink-muted">
          {order.fulfillmentType} · <span className="tabular-nums">{format(order.total)}</span>
        </div>
        {/* ADR-0021 §2.2 — deliberately no "awaiting confirmation" / "held for
            waiter" badge here: a call-centre order never enters that state. */}
      </div>

      {canTransfer && (
        <div className="flex items-end gap-2 rounded-lg border p-3">
          <div className="min-w-40 flex-1">
            <Field label={t('callCenter.desk.transferTo')}>
              <Select value={transferBranchId} onChange={(e) => onTransferBranchChange(e.target.value)}>
                <option value="">{t('callCenter.desk.pickBranch')}</option>
                {branches.filter((b) => b.id !== order.branchId).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.code})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Button variant="outline" disabled={!transferBranchId || transferring} onClick={onTransfer}>
            {t('callCenter.desk.transfer')}
          </Button>
        </div>
      )}

      <Button variant="outline" className="w-full" onClick={onNewOrder}>
        {t('callCenter.desk.newOrder')}
      </Button>
    </div>
  );
}
